import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type { M3ApiResponse } from '@/types/api';
import type {
  Country,
  ManualIdVerificationRecord,
  ManualIdVerificationStats,
} from '@/types/manual-id';

const RECORDS_KEY = ['manual-id-verifications'] as const;
const STATS_KEY = ['manual-id-verifications', 'stats'] as const;
const COUNTRIES_KEY = ['countries'] as const;

interface GetListParams {
  startDate: string;
  endDate: string;
  status: string;
}

export function useManualIdVerificationsQuery(params: GetListParams) {
  return useQuery({
    queryKey: [...RECORDS_KEY, params],
    queryFn: async () => {
      const { data } = await apiClient.post<
        M3ApiResponse<{ records: ManualIdVerificationRecord[] }>
      >(`${appConfig.airtableApi}/getManualIdVerifications`, params);
      return data.result?.records ?? [];
    },
  });
}

export function useManualIdStatsQuery() {
  return useQuery({
    queryKey: STATS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<ManualIdVerificationStats>>(
        `${appConfig.airtableApi}/statsGetManualIdVerifications`,
      );
      return data.result;
    },
  });
}

export function useCountriesQuery() {
  return useQuery({
    queryKey: COUNTRIES_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<Country[]>>(
        `${appConfig.shopsManagementApi}/countries`,
      );
      return data.result ?? [];
    },
    staleTime: 10 * 60 * 1000, // countries rarely change
  });
}

export function useVerifyManualIdMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (record: ManualIdVerificationRecord) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.identityVerificationApi}/verifyManualVerificationStatus`,
        record,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RECORDS_KEY }),
  });
}

export function useRejectManualIdMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (record: ManualIdVerificationRecord) => {
      const { data } = await apiClient.post<M3ApiResponse>(
        `${appConfig.identityVerificationApi}/rejectManualVerificationStatus`,
        record,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RECORDS_KEY }),
  });
}
